import { useMemo } from 'react';
import {
    useLocale,
    useContentEditI18n,
    normalizeContentLocale,
    type I18nConfig,
} from '@ai-content-hub/content-core';
import type { ElementRuntimeConfigSlice } from '@ai-content-hub/content-kit';
import { parentI18nFromElementConfig } from './parentI18nFromElementConfig';

/** Per-element `schema.i18n` shape (`{ en: {…}, vi: {…} }`). */
export type ElementSchemaLanguageMap = Record<string, Record<string, string>>;

export interface UseElementSchemaLocaleOptions {
    config?: ElementRuntimeConfigSlice;
    language?: string;
}

/**
 * Resolve copy from the element `schema.i18n` map (essay-style).
 * Parent `config.i18n` is used only for default/initial language, not merged string tables.
 */
export function useElementSchemaLocale(
    schemaI18n: ElementSchemaLanguageMap | undefined,
    options: UseElementSchemaLocaleOptions = {}
) {
    const { config, language } = options;
    const editI18n = useContentEditI18n();
    const parentI18n =
        parentI18nFromElementConfig(config) ??
        parentI18nFromElementConfig(
            editI18n?.i18n ? ({ i18n: editI18n.i18n } as ElementRuntimeConfigSlice) : undefined
        );

    const resolvedLanguage = normalizeContentLocale(
        language ?? editI18n?.language ?? parentI18n?.defaultLanguage
    );

    const localeConfig = useMemo((): I18nConfig | undefined => {
        if (!schemaI18n) {
            return parentI18n;
        }
        return {
            defaultLanguage: resolvedLanguage,
            languages: schemaI18n,
        };
    }, [schemaI18n, parentI18n, resolvedLanguage]);

    return useLocale({
        config: localeConfig,
        initialLanguage: resolvedLanguage,
    });
}
